// components/LocaleUrlSync.jsx
'use client'

import { useEffect, useRef } from 'react'
import { usePathname } from 'next/navigation'
import { TODOS } from '@/lib/idiomas'

const SUPPORTED_LOCALES = TODOS

/**
 * Componente que sincroniza la URL del navegador con el locale actual.
 * 
 * Cuando el usuario entra a una URL sin locale (ej: "/libros"), el servidor 
 * hace un rewrite interno a "/[locale]/libros" para evitar un redirect. 
 * Este componente detecta esa situación y usa history.replaceState para 
 * actualizar la URL a "/es/libros", "/en/libros", etc.
 * 
 * También detecta navegación interna sin locale y la corrige.
 */
export default function LocaleUrlSync({ locale }) {
  // usePathname para detectar cambios de navegación interna
  const nextPathname = usePathname()
  const lastSyncedRef = useRef('')

  useEffect(() => {
    if (typeof window === 'undefined') return

    const currentPath = window.location.pathname
    const currentSearch = window.location.search
    const segments = currentPath.split('/')
    const firstSegment = segments[1] || ''

    const hasLocaleInUrl = SUPPORTED_LOCALES.includes(firstSegment)

    if (!hasLocaleInUrl) {
      const newPath = `/${locale}${currentPath === '/' ? '' : currentPath}`
      const newUrl = `${newPath}${currentSearch}`

      // Evitar llamar replaceState si ya sincronizamos esta URL
      if (lastSyncedRef.current === newUrl) return
      lastSyncedRef.current = newUrl

      window.history.replaceState(
        { ...window.history.state, locale },
        '',
        newUrl
      )
    }
  }, [locale, nextPathname]) // Re-ejecutar cuando cambia el pathname de Next.js

  return null
}
